import {
  OportunidadPipelineItem,
  PipelinePlataformaService,
} from "../../../shared/generated/grpc/plataforma_ventas_pipeline_pb";
import { AgendaResponse } from "../../../shared/response/ventas/AgendaResponse";
import {
  EventoTipo,
  OportunidadListResponse,
  OportunidadResponse,
} from "../../../shared/response/ventas/OportunidadResponse";
import { PipelineStatsResponse } from "../../../shared/response/ventas/PipelineStatsResponse";
import { PreciosResponse } from "../../../shared/response/ventas/PreciosResponse";
import { ServiciosResponse } from "../../../shared/response/ventas/ServiciosResponse";
import { EventoPayload } from "../../../shared/services/api/payload/EventoPayload";
import { ExtenderDemoPayload } from "../../../shared/services/api/payload/ExtenderDemoPayload";
import { OportunidadSavePayload } from "../../../shared/services/api/payload/OportunidadSavePayload";
import { OportunidadStagePayload } from "../../../shared/services/api/payload/OportunidadStagePayload";
import { RenovarOportunidadPayload } from "../../../shared/services/api/payload/RenovarOportunidadPayload";
import { CostoAgriculturaListService } from "../../../shared/services/costos/CostoAgriculturaListService";
import { CostoAspersionListService } from "../../../shared/services/costos/CostoAspersionListService";
import { CostoEquipoListService } from "../../../shared/services/costos/CostoEquipoListService";
import { CostoFotogrametriaListService } from "../../../shared/services/costos/CostoFotogrametriaListService";
import { CostoGanaderiaListService } from "../../../shared/services/costos/CostoGanaderiaListService";
import { dateToMs, grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";
import { ProductosMarca } from "../../productosMarca";

/**
 * Módulo Pipeline de Ventas (`PipelinePlataformaService`, gRPC-Web).
 * Precios y servicios los carga `PipelinePage._loadData()` la primera vez que se abre
 * el módulo y quedan en memoria el resto de la sesión.
 */
export class PipelineService {
  static readonly instance = new PipelineService();
  private constructor() {}

  // ── Caché de precios (válido por sesión) ──────────────────────────────────
  private _precios: PreciosResponse | null = null;
  get precios(): PreciosResponse | null { return this._precios; }

  /**
   * Tarifas para cotizar: reusa los listados del menú Precios (`CostosPlataformaService`), que quedan en memoria. Los
   * de lo que la marca no vende (`productos`, 2026-10-20) no se piden y quedan vacíos.
   */
  async loadPrecios(productos: ProductosMarca): Promise<void> {
    const agricultura = CostoAgriculturaListService.instance;
    const ganaderia = CostoGanaderiaListService.instance;
    const protec = CostoAspersionListService.instance;
    const equipos = CostoEquipoListService.instance;
    const vista = CostoFotogrametriaListService.instance;
    const conProtec = productos.ofrece("protec");
    const conEquipos = productos.ofrece("rastro") || productos.ofrece("bascula");
    const conVista = productos.ofrece("vista");
    // `sync()` propaga el error (a diferencia de `get()`), como el antiguo GET /precios.
    await Promise.all([
      agricultura.sync(),
      ganaderia.sync(),
      conProtec ? protec.sync() : null,
      conEquipos ? equipos.sync() : null,
      conVista ? vista.sync() : null,
    ]);
    this._precios = {
      agricultura: agricultura.items,
      ganaderia: ganaderia.items,
      protec: conProtec ? protec.items : [],
      equipos: conEquipos ? equipos.items : [],
      vista: conVista ? vista.items : [],
    };
  }

  // ── Caché de servicios/productos ────────────────────────────────────────────

  private _servicios: ServiciosResponse | null = null;
  get servicios(): ServiciosResponse | null { return this._servicios; }

  /** Catálogo fijo de servicios cotizables (`ListServicios`). */
  async loadServicios(): Promise<void> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).listServicios({}));
    this._servicios = {
      data: r.items.map((s) => ({
        id: s.id,
        nombre: s.nombre,
        descripcion: s.descripcion,
        configurable: s.configurable,
        campos: s.campos.length > 0
          ? s.campos.map((c) => ({
            clave: c.clave,
            tipo: c.tipo,
            min: c.min ?? null,
            label: c.label,
            opciones: c.opciones.length > 0 ? c.opciones : null,
            fuente: c.fuente ?? null,
          }))
          : null,
      })),
    };
  }

  // ── Oportunidades ───────────────────────────────────────────────────────────

  /** Oportunidades no eliminadas con sus eventos (`ListOportunidades`); `q` busca por nombre o NIT del cliente. */
  async getOportunidades(filters: {
    q?: string;
    stage?: string;
    source?: string;
    comercialId?: string;
    clienteId?: string;
  } = {}): Promise<OportunidadListResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).listOportunidades({
      q: filters.q || undefined,
      stage: filters.stage || undefined,
      source: filters.source || undefined,
      comercialId: filters.comercialId || undefined,
      clienteId: filters.clienteId || undefined,
    }));
    const data = r.items.map(toOportunidadResponse);
    return { data, total: data.length };
  }

  /** Alta (nace en "new") o edición (`GuardarOportunidad`). Notas y valor omitidos se conservan. */
  async saveOportunidad(payload: OportunidadSavePayload): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).guardarOportunidad({
      id: payload.id,
      clienteId: payload.clienteId,
      source: payload.source,
      stage: payload.id ? payload.stage : undefined,
      comercialId: payload.comercialId ?? undefined,
      proxContacto: dateToMs(payload.proxContacto),
      notas: payload.notas,
      valorCierre: payload.valorCierre != null ? BigInt(Math.round(payload.valorCierre)) : undefined,
      productos: payload.productos ?? undefined,
    }));
    return toOportunidadResponse(r);
  }

  /** Cambia la etapa (`CambiarEtapa`); la nota opcional queda también como evento. */
  async changeStage(id: string, payload: OportunidadStagePayload): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).cambiarEtapa({
      id,
      stage: payload.stage,
      nota: payload.nota,
    }));
    return toOportunidadResponse(r);
  }

  /** Evento manual (`AgregarEvento`); la metadata viaja como JSON. */
  async addEvento(id: string, payload: EventoPayload): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).agregarEvento({
      id,
      tipo: payload.tipo,
      metadataJson: payload.metadata ? JSON.stringify(payload.metadata) : undefined,
    }));
    return toOportunidadResponse(r);
  }

  /** Baja lógica (`EliminarOportunidad`), idempotente. */
  async deleteOportunidad(id: string): Promise<void> {
    await grpcCall(() => grpcClient(PipelinePlataformaService).eliminarOportunidad({ id }));
  }

  // ── Demo y renovación ─────────────────────────────────────────────────────

  /** Inicia la demo de plataforma (demoFin = ahora + 30 días). */
  async asignarDemo(id: string): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).asignarDemo({ id }));
    return toOportunidadResponse(r);
  }

  /** Suma días al fin de la demo. */
  async extenderDemo(id: string, payload: ExtenderDemoPayload): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).extenderDemo({ id, dias: payload.dias }));
    return toOportunidadResponse(r);
  }

  /** Reactiva el plan con la nueva fecha de renovación. */
  async renovarOportunidad(id: string, payload: RenovarOportunidadPayload): Promise<OportunidadResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).renovar({
      id,
      renovationAt: BigInt(Date.parse(payload.renovationAt)),
    }));
    return toOportunidadResponse(r);
  }

  // ── Stats y Agenda ────────────────────────────────────────────────────────

  /** KPIs del tablero (`GetStats`). */
  async getStats(): Promise<PipelineStatsResponse> {
    const s = await grpcCall(() => grpcClient(PipelinePlataformaService).getStats({}));
    return {
      oportunidadesActivas: s.oportunidadesActivas,
      demosAgendadas: s.demosAgendadas,
      cerradasGanadas: s.cerradasGanadas,
      cerradasPerdidas: s.cerradasPerdidas,
      tasaConversion: s.tasaConversion,
      embudo: s.embudo.map((e) => ({ stage: e.stage, label: e.label, count: e.count })),
      porFuente: s.porFuente.map((f) => ({ source: f.source, count: f.count })),
      rendimientoComerciales: s.rendimientoComerciales.map((c) => ({ comercial: c.comercial, leads: c.leads, cerrados: c.cerrados })),
    };
  }

  /** Agenda entre dos fechas ISO (`GetAgenda`): demos, pendientes y vencidos. */
  async getAgenda(desde: string, hasta: string): Promise<AgendaResponse> {
    const r = await grpcCall(() => grpcClient(PipelinePlataformaService).getAgenda({
      desde: BigInt(Date.parse(desde)),
      hasta: BigInt(Date.parse(hasta)),
    }));
    return {
      demos: r.demos.map(toOportunidadResponse),
      pendientes: r.pendientes.map(toOportunidadResponse),
      vencidos: r.vencidos.map(toOportunidadResponse),
    };
  }
}

/** Unix ms opcional → ISO 8601 o null. */
function msToIso(ms: bigint | undefined): string | null {
  return ms !== undefined ? new Date(Number(ms)).toISOString() : null;
}

/** Metadata JSON del evento → objeto; null si no se puede leer. */
function parseMetadata(json: string): Record<string, unknown> | null {
  try { return JSON.parse(json) as Record<string, unknown>; } catch { return null; }
}

/** Mensaje gRPC de la oportunidad → `OportunidadResponse` (fechas en ISO, como las usa la página). */
function toOportunidadResponse(o: OportunidadPipelineItem): OportunidadResponse {
  return {
    id: o.id,
    clienteId: o.clienteId,
    clienteNombre: o.clienteNombre,
    clienteNit: o.clienteNit ?? null,
    source: o.source,
    stage: o.stage,
    comercialId: o.comercialId ?? null,
    comercialNombre: o.comercialNombre ?? null,
    proxContacto: msToIso(o.proxContacto),
    notas: o.notas,
    activatedAt: msToIso(o.activatedAt),
    renovationAt: msToIso(o.renovationAt),
    demoFin: msToIso(o.demoFin),
    valorCierre: o.valorCierre !== undefined ? Number(o.valorCierre) : null,
    productos: o.productos
      ? {
        dataag: o.productos.dataag,
        rastro: o.productos.rastro,
        bascula: o.productos.bascula,
        vista: o.productos.vista,
        protec: {
          activo: o.productos.protec?.activo ?? false,
          cat: o.productos.protec?.cat ?? "",
          ha: o.productos.protec?.ha ?? 0,
        },
      }
      : null,
    eventos: o.eventos.map((e) => ({
      id: e.id,
      tipo: e.tipo as EventoTipo,
      metadata: parseMetadata(e.metadataJson),
      fecha: new Date(Number(e.fecha)).toISOString(),
      usuarioNombre: e.usuarioNombre ?? null,
    })),
    createdAt: Number(o.createdAt),
  };
}
